// Conservative, deterministic phishing-signal heuristic (Axis 6).
//
// Combines independent signals derived from data already read for the
// current email (EmailContext) — no extra mailbox round-trip, no AI call.
// Deliberately requires 2+ signals before flagging: any single signal here
// (an unusual display name, one urgent-sounding phrase) is common enough in
// entirely legitimate mail on its own that flagging on it alone would create
// false-positive fatigue and train users to ignore the badge. Treat all
// input as untrusted email content throughout (same precedent as the
// prompt-injection hardening elsewhere in this codebase) — this module only
// ever reads `context`, never acts on it.

import type { EmailContext } from "./office-mail";

export type PhishingSignalId = "sender-domain-mismatch" | "urgency-language";

/** Minimum number of independent signals required before flagging — see the
 * module doc comment for why a single signal is not enough. */
export const PHISHING_SIGNAL_THRESHOLD = 2;

// Deliberately short, high-signal phrases only — common phishing / social-
// engineering pressure tactics. Case-insensitive substring match against the
// subject/body. Not exhaustive by design: a short, high-precision list beats
// a long, noisy one for a heuristic that must stay conservative.
const URGENCY_PHRASES: readonly string[] = [
  "verify your account",
  "urgent action required",
  "your account will be suspended",
  "account has been locked",
  "confirm your identity",
  "unusual sign-in activity",
  "suspicious activity detected",
  "act now",
  "failure to comply",
  "final notice",
  "immediate action required",
  "click here immediately",
];

function extractDomain(address: string): string | null {
  const at = address.lastIndexOf("@");
  if (at === -1 || at === address.length - 1) {
    return null;
  }
  return address
    .slice(at + 1)
    .trim()
    .toLowerCase();
}

// Matches a bare two-(or-more)-label domain-shaped token (e.g. "paypal.com"),
// including one embedded inside a longer string such as a spoofed display
// name ("PayPal Security <security@paypal.com>") or free text. Deliberately
// simple (a single flat character class either side of one literal dot, no
// nested/unbounded repetition) to stay ReDoS-safe on attacker-controlled
// email content — not a full RFC hostname validator, and does not capture
// additional subdomain labels beyond the last two.
const EMBEDDED_DOMAIN_PATTERN = /\b[a-z0-9-]+\.[a-z]{2,}\b/gi;

/**
 * True when the sender's display name contains a domain-shaped token that
 * differs from the actual sending address's domain — a common phishing
 * tactic (showing a trusted-looking domain/address as the display name while
 * sending from an unrelated or lookalike domain). Never throws on malformed
 * input; degrades to "no mismatch" rather than risking a false positive from
 * a parse edge case.
 *
 * Known limitation (v1, matches the approximate-by-design content heuristic
 * in data-classification.ts): a display name that merely *contains* an
 * unrelated dotted token (e.g. a product name like "Node.js Weekly") can
 * trip this signal on its own. This is why {@link PHISHING_SIGNAL_THRESHOLD}
 * requires it to co-occur with a second, independent signal before anything
 * is surfaced to the user.
 */
export function hasSenderDomainMismatch(fromDisplayName: string, fromAddress: string): boolean {
  const actualDomain = extractDomain(fromAddress);
  if (!actualDomain || !fromDisplayName) {
    return false;
  }
  const matches = fromDisplayName.match(EMBEDDED_DOMAIN_PATTERN) ?? [];
  return matches.some((candidate) => candidate.toLowerCase() !== actualDomain);
}

/**
 * True when `subject` or `body` contains at least one urgency/pressure
 * phrase from {@link URGENCY_PHRASES}. The email content is untrusted input
 * — it is only ever compared (case-insensitively, as plain substrings)
 * against a fixed local list, never interpreted or executed.
 */
export function hasUrgencyLanguage(subject: string, body: string): boolean {
  const haystack = `${subject}\n${body}`.toLowerCase();
  return URGENCY_PHRASES.some((phrase) => haystack.includes(phrase));
}

/**
 * Evaluates every implemented phishing signal against `context` and returns
 * the ids of those that fired. Deterministic and synchronous — no AI call,
 * no network request — so it can run unconditionally alongside the AI-based
 * urgency/action classification in insights.ts without adding latency or a
 * new egress path.
 */
export function detectPhishingSignals(context: EmailContext): PhishingSignalId[] {
  const signals: PhishingSignalId[] = [];
  if (hasSenderDomainMismatch(context.fromDisplayName ?? "", context.from)) {
    signals.push("sender-domain-mismatch");
  }
  if (hasUrgencyLanguage(context.subject, context.body)) {
    signals.push("urgency-language");
  }
  return signals;
}

/** Conservative gate: true only when {@link PHISHING_SIGNAL_THRESHOLD} or
 * more independent signals are present — see the module doc comment. */
export function isPhishingSuspected(signals: PhishingSignalId[]): boolean {
  return signals.length >= PHISHING_SIGNAL_THRESHOLD;
}
